import React, { useRef, useState, useLayoutEffect, useCallback } from 'react';
import { TextField, InputAdornment, IconButton, Box, Typography } from '@mui/material';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import CameraAltIcon from '@mui/icons-material/CameraAlt';

interface WmsBarcodeScannerProps {
  onScan: (barcode: string) => void;
  placeholder?: string;
  autoFocus?: boolean;
  mode?: 'wedge' | 'camera';
  disabled?: boolean;
  size?: 'small' | 'medium';
}

const MIN_BARCODE_LENGTH = 3;

const WmsBarcodeScanner: React.FC<WmsBarcodeScannerProps> = ({
  onScan,
  placeholder = 'Barkod okutun...',
  autoFocus = true,
  mode = 'wedge',
  disabled = false,
  size = 'medium',
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [buffer, setBuffer] = useState('');
  const bufferRef = useRef('');

  // Paint'ten önce senkron focus: scanner her mount'ta hemen hazır olur.
  useLayoutEffect(() => {
    if (autoFocus && !disabled && mode === 'wedge') {
      inputRef.current?.focus();
    }
  }, [autoFocus, disabled, mode]);

  // onScan değişince (step geçişlerinde) mevcut input odağını koru.
  // Sadece ref güncellemesi — hiçbir DOM yan etkisi yok.
  const onScanRef = useRef(onScan);
  useLayoutEffect(() => {
    onScanRef.current = onScan;
  }, [onScan]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (disabled) return;
    if (e.key === 'Enter') {
      e.preventDefault();
      const value = bufferRef.current.trim();
      bufferRef.current = '';
      setBuffer('');
      if (value.length >= MIN_BARCODE_LENGTH) {
        onScanRef.current(value);
      }
    }
  }, [disabled]);

  const handleManualSubmit = useCallback(() => {
    const value = buffer.trim();
    if (value.length >= MIN_BARCODE_LENGTH) {
      bufferRef.current = '';
      setBuffer('');
      onScanRef.current(value);
      inputRef.current?.focus();
    }
  }, [buffer]);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (disabled) return;
    const next = e.target.value ?? '';
    bufferRef.current = next;
    setBuffer(next);
  }, [disabled]);

  if (mode === 'camera') {
    return (
      <Box sx={{ textAlign: 'center', p: 2 }}>
        <CameraAltIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
        <Typography color="text.secondary" variant="body2">
          Kamera modu ileride etkinleştirilecektir.
        </Typography>
      </Box>
    );
  }

  return (
    <TextField
      inputRef={inputRef}
      value={buffer}
      onKeyDown={handleKeyDown}
      onChange={handleInputChange}
      placeholder={placeholder}
      fullWidth
      size={size}
      disabled={disabled}
      inputProps={{ autoCapitalize: 'off', autoCorrect: 'off', spellCheck: 'false' }}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <QrCodeScannerIcon color={disabled ? 'disabled' : 'primary'} />
          </InputAdornment>
        ),
        endAdornment: buffer.length >= MIN_BARCODE_LENGTH ? (
          <InputAdornment position="end">
            <IconButton size="small" onClick={handleManualSubmit} edge="end">
              <Typography variant="caption" sx={{ fontWeight: 700 }}>OK</Typography>
            </IconButton>
          </InputAdornment>
        ) : undefined,
        sx: size === 'medium' ? { fontSize: 18, height: 56 } : undefined,
      }}
      sx={{
        '& .MuiOutlinedInput-root': {
          bgcolor: 'background.paper',
        },
      }}
    />
  );
};

export default WmsBarcodeScanner;
